import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, DollarSign, TrendingUp, Activity, Search, Filter, Download, Edit, Trash2, Eye, MoreVertical, Info } from 'lucide-react';
import { AdminHeader } from '../../components/AdminHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';

const mockCustomers = [
  { id: 1, name: 'John Doe', email: 'john@example.com', company: 'Tech Corp', status: 'Active', revenue: '$25,000', projects: 3, joinDate: '2024-01-15' },
  { id: 2, name: 'Sarah Smith', email: 'sarah@example.com', company: 'Digital Solutions', status: 'Active', revenue: '$45,000', projects: 5, joinDate: '2024-02-20' },
  { id: 3, name: 'Michael Johnson', email: 'michael@example.com', company: 'Innovate Inc', status: 'Pending', revenue: '$15,000', projects: 2, joinDate: '2024-03-10' },
  { id: 4, name: 'Emily Brown', email: 'emily@example.com', company: 'StartUp Labs', status: 'Active', revenue: '$35,000', projects: 4, joinDate: '2024-01-25' },
  { id: 5, name: 'David Wilson', email: 'david@example.com', company: 'Enterprise Solutions', status: 'Active', revenue: '$55,000', projects: 6, joinDate: '2024-02-05' },
  { id: 6, name: 'Lisa Anderson', email: 'lisa@example.com', company: 'Growth Ventures', status: 'Inactive', revenue: '$10,000', projects: 1, joinDate: '2024-03-15' },
  { id: 7, name: 'Robert Taylor', email: 'robert@example.com', company: 'Cloud Systems', status: 'Active', revenue: '$40,000', projects: 5, joinDate: '2024-01-30' },
  { id: 8, name: 'Jennifer Martinez', email: 'jennifer@example.com', company: 'Data Insights', status: 'Active', revenue: '$30,000', projects: 3, joinDate: '2024-02-15' },
];

export const Dashboard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customers, setCustomers] = useState(mockCustomers);
  const [selectedView, setSelectedView] = useState('table'); // 'table' or 'grid'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = [
    { title: 'Total Customers', value: '248', icon: Users, change: '+12%', color: '#00a8ff' },
    { title: 'Total Revenue', value: '$1.2M', icon: DollarSign, change: '+23%', color: '#4cd3ff' },
    { title: 'Active Projects', value: '42', icon: TrendingUp, change: '+8%', color: '#00a8ff' },
    { title: 'Conversion Rate', value: '67%', icon: Activity, change: '+5%', color: '#4cd3ff' },
  ];

  return (
    <>
      <AdminHeader />
      <div className="min-h-screen py-8 px-4 md:px-8 pt-24">
        {/* Insight/Overview Box */}
        <Card className="mb-8 bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl backdrop-blur-sm animate-slide-up" style={{ boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[rgba(0,168,255,0.3)] rounded-lg flex items-center justify-center" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.5)' }}>
                <Info className="text-[#00a8ff]" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-1" style={{ textShadow: '0 0 8px #00a8ff' }}>Dashboard Overview</h3>
                <p className="text-[#00a8ff] text-sm" style={{ textShadow: '0 0 5px #00a8ff' }}>Monitor your business metrics and customer data in real-time. Click the eye icon to view detailed customer information.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="animate-slide-up">
            <h1 className="text-4xl font-bold led-text animate-led-pulse mb-2" style={{ textShadow: '0 0 15px #00a8ff, 0 0 30px #4cd3ff' }}>
              ADMIN DASHBOARD
            </h1>
            <p className="text-[#00a8ff]" style={{ textShadow: '0 0 8px #00a8ff' }}>Welcome back, Administrator</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admin/entry-form">
              <Button className="bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold transition-all duration-300 hover:scale-105" style={{ boxShadow: '0 0 20px #00a8ff' }}>
                + New Entry Form
              </Button>
            </Link>
          </div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <Card
            key={index}
            className="animate-on-scroll opacity-0 bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl backdrop-blur-sm hover:shadow-[0_0_30px_#00a8ff] transition-all duration-500 hover:-translate-y-2 group"
            style={{ transitionDelay: `${index * 100}ms`, transform: 'translateY(30px)', boxShadow: '0 0 15px rgba(0,168,255,0.3)' }}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-[rgba(0,168,255,0.2)] rounded-lg flex items-center justify-center group-hover:bg-[#00a8ff] group-hover:scale-110 transition-all duration-300" style={{ boxShadow: '0 0 15px rgba(0,168,255,0.5)' }}>
                  <stat.icon className="text-[#00a8ff] group-hover:text-[#0a2540] transition-colors" size={24} />
                </div>
                <Badge className="bg-green-500/20 text-green-400 border-green-500">{stat.change}</Badge>
              </div>
              <h3 className="text-sm text-[#00a8ff] mb-2" style={{ textShadow: '0 0 5px #00a8ff' }}>{stat.title}</h3>
              <p className="text-3xl font-bold led-text" style={{ textShadow: '0 0 10px #00a8ff' }}>{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Customers Section */}
      <Card className="animate-on-scroll opacity-0 bg-[rgba(30,64,98,0.7)] border border-[#00a8ff] rounded-xl backdrop-blur-sm" style={{ transitionDelay: '400ms', transform: 'translateY(30px)', boxShadow: '0 0 20px rgba(0,168,255,0.3)' }}>
        <CardContent className="p-6">
          {/* Controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <h2 className="text-2xl font-bold led-text" style={{ textShadow: '0 0 10px #00a8ff' }}>CUSTOMER RECORDS</h2>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:flex-initial">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00a8ff]" size={18} />
                <Input
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[rgba(10,37,64,0.7)] border-[#00a8ff] text-white w-full md:w-64"
                  style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                />
              </div>
              <Button className="bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all" style={{ boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}>
                <Filter size={18} className="mr-2" />
                Filter
              </Button>
              <Button className="bg-[#00a8ff] text-[#0a2540] hover:bg-[#4cd3ff] font-bold transition-all" style={{ boxShadow: '0 0 15px #00a8ff' }}>
                <Download size={18} className="mr-2" />
                Export
              </Button>
              <div className="flex border border-[#00a8ff] rounded-md overflow-hidden">
                <button
                  onClick={() => setSelectedView('table')}
                  className={`px-4 py-2 transition-all ${
                    selectedView === 'table'
                      ? 'bg-[#00a8ff] text-[#0a2540]'
                      : 'bg-transparent text-[#00a8ff] hover:bg-[rgba(0,168,255,0.2)]'
                  }`}
                >
                  Table
                </button>
                <button
                  onClick={() => setSelectedView('grid')}
                  className={`px-4 py-2 transition-all border-l border-[#00a8ff] ${
                    selectedView === 'grid'
                      ? 'bg-[#00a8ff] text-[#0a2540]'
                      : 'bg-transparent text-[#00a8ff] hover:bg-[rgba(0,168,255,0.2)]'
                  }`}
                >
                  Grid
                </button>
              </div>
            </div>
          </div>

          {/* Table View */}
          {selectedView === 'table' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#00a8ff]">
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>ID</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Name</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Email</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Company</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Status</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Revenue</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Projects</th>
                    <th className="text-left py-4 px-4 text-[#00a8ff] font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer, index) => (
                    <tr
                      key={customer.id}
                      className="border-b border-[rgba(0,168,255,0.2)] hover:bg-[rgba(0,168,255,0.1)] transition-colors duration-200"
                      style={{ animation: `fadeIn 0.3s ease ${index * 0.05}s backwards` }}
                    >
                      <td className="py-4 px-4 text-white" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.id}</td>
                      <td className="py-4 px-4 text-white font-medium" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.name}</td>
                      <td className="py-4 px-4 text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.email}</td>
                      <td className="py-4 px-4 text-white" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.company}</td>
                      <td className="py-4 px-4">
                        <Badge className={`${
                          customer.status === 'Active' ? 'bg-green-500/20 text-green-400 border-green-500' :
                          customer.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500' :
                          'bg-red-500/20 text-red-400 border-red-500'
                        }`}>
                          {customer.status}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-white font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.revenue}</td>
                      <td className="py-4 px-4 text-[#00a8ff]" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.projects}</td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2">
                          <button onClick={() => handleViewDetails(customer)} className="p-2 rounded-md bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all" title="View">
                            <Eye size={16} />
                          </button>
                          <button className="p-2 rounded-md bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all" title="Edit">
                            <Edit size={16} />
                          </button>
                          <button className="p-2 rounded-md bg-[rgba(255,0,0,0.2)] border border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-all" title="Delete">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Grid View */}
          {selectedView === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCustomers.map((customer, index) => (
                <Card
                  key={customer.id}
                  className="bg-[rgba(10,37,64,0.7)] border border-[#00a8ff] rounded-xl hover:shadow-[0_0_20px_#00a8ff] transition-all duration-300 hover:-translate-y-2"
                  style={{ animation: `fadeIn 0.3s ease ${index * 0.05}s backwards`, boxShadow: '0 0 10px rgba(0,168,255,0.3)' }}
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-12 h-12 rounded-full bg-[rgba(0,168,255,0.3)] flex items-center justify-center font-bold text-[#00a8ff]" style={{ boxShadow: '0 0 10px #00a8ff' }}>
                        {customer.name.charAt(0)}
                      </div>
                      <Badge className={`${
                        customer.status === 'Active' ? 'bg-green-500/20 text-green-400 border-green-500' :
                        customer.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500' :
                        'bg-red-500/20 text-red-400 border-red-500'
                      }`}>
                        {customer.status}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1" style={{ textShadow: '0 0 8px #00a8ff' }}>{customer.name}</h3>
                    <p className="text-sm text-[#00a8ff] mb-1" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.email}</p>
                    <p className="text-sm text-white mb-4" style={{ textShadow: '0 0 5px #00a8ff' }}>{customer.company}</p>
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <p className="text-xs text-[#00a8ff]">Revenue</p>
                        <p className="text-lg font-bold led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>{customer.revenue}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#00a8ff]">Projects</p>
                        <p className="text-lg font-bold led-text" style={{ textShadow: '0 0 8px #00a8ff' }}>{customer.projects}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="flex-1 py-2 rounded-md bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all text-sm font-bold">
                        View
                      </button>
                      <button className="flex-1 py-2 rounded-md bg-[rgba(0,168,255,0.2)] border border-[#00a8ff] text-[#00a8ff] hover:bg-[#00a8ff] hover:text-[#0a2540] transition-all text-sm font-bold">
                        Edit
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

        {/* Customer Details Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="bg-[rgba(10,37,64,0.98)] border-2 border-[#00a8ff] text-white max-w-2xl" style={{ boxShadow: '0 0 40px rgba(0,168,255,0.5)' }}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold led-text" style={{ textShadow: '0 0 15px #00a8ff' }}>
                Customer Details
              </DialogTitle>
            </DialogHeader>
            {selectedCustomer && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Name</p>
                    <p className="text-white font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>{selectedCustomer.name}</p>
                  </div>
                  <div className="bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Email</p>
                    <p className="text-white font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>{selectedCustomer.email}</p>
                  </div>
                  <div className="bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Company</p>
                    <p className="text-white font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>{selectedCustomer.company}</p>
                  </div>
                  <div className="bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Status</p>
                    <Badge className={`${
                      selectedCustomer.status === 'Active' ? 'bg-green-500/20 text-green-400 border-green-500' :
                      selectedCustomer.status === 'Pending' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500' :
                      'bg-red-500/20 text-red-400 border-red-500'
                    }`}>
                      {selectedCustomer.status}
                    </Badge>
                  </div>
                  <div className="bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Total Revenue</p>
                    <p className="text-white font-bold text-xl" style={{ textShadow: '0 0 8px #00a8ff' }}>{selectedCustomer.revenue}</p>
                  </div>
                  <div className="bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Active Projects</p>
                    <p className="text-white font-bold text-xl" style={{ textShadow: '0 0 8px #00a8ff' }}>{selectedCustomer.projects}</p>
                  </div>
                  <div className="col-span-2 bg-[rgba(0,168,255,0.1)] p-4 rounded-lg border border-[#00a8ff]">
                    <p className="text-[#00a8ff] text-sm mb-1">Join Date</p>
                    <p className="text-white font-bold" style={{ textShadow: '0 0 5px #00a8ff' }}>
                      {new Date(selectedCustomer.joinDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-on-scroll {
            transition: opacity 0.7s ease, transform 0.7s ease;
          }
          .animate-on-scroll.visible {
            opacity: 1 !important;
            transform: translateY(0) !important;
          }
        `}</style>
      </div>
    </>
  );
};